import { AlarmClock, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertDto } from '@/Domain/api/Conversation/dto/Alert.dto';
import { MetadataRunConditionDto } from '@/Domain/api/Shared/dto/MetadataRunCondition.dto';
import { AlertForm } from '@/Domain/components/Insights/Alerts/AlertForm/AlertForm';
import { AlertRunConditions } from '@/Domain/components/Insights/Alerts/AlertForm/AlertRunConditions';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Field, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { TooltipProvider } from '@/Domain/components/Shared/Tooltip/Tooltip';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { areAlertRunConditionsComplete } from '@/Domain/helper/alertRunCondition.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useConversationAnalysisAlertCreate } from '@/Domain/hooks/Conversation/useConversationAnalysisAlertCreate.hook';

type Props = {
  alerts: AlertDto[];
};

const MAX_ALERTS = 10;

export const ManageAlertsPage = ({ alerts }: Props) => {
  const { t } = useTranslation('insights_page');
  const { handleCreateAlert, isPending } = useConversationAnalysisAlertCreate();
  const { capture } = usePostHogAnalytics();

  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftRunConditions, setDraftRunConditions] = useState<MetadataRunConditionDto[]>([]);
  const [draftError, setDraftError] = useState<string | null>(null);

  const isFull = alerts.length >= MAX_ALERTS;
  const remaining = MAX_ALERTS - alerts.length;
  const canSubmit =
    !!draftName.trim() &&
    !!draftDescription.trim() &&
    areAlertRunConditionsComplete(draftRunConditions) &&
    !isFull &&
    !isPending;

  const handleAdd = async () => {
    if (!canSubmit) return;
    try {
      await handleCreateAlert({
        name: draftName.trim(),
        description: draftDescription.trim(),
        run_conditions: draftRunConditions,
      });
      capture(PostHogEventEnum.AlertCreated, {
        name_length: draftName.trim().length,
        description_length: draftDescription.trim().length,
      });
      setDraftName('');
      setDraftDescription('');
      setDraftRunConditions([]);
      setDraftError(null);
    } catch {
      setDraftError(t('manage_alerts.add_error'));
    }
  };

  return (
    <div className="@container flex min-w-0 flex-col gap-6 py-6">
      <div className="rounded-xl bg-muted">
        <div className="py-6 px-6 flex items-start gap-3">
          <AlarmClock className="bg-foreground-subtle rounded-full text-white p-1 size-3.5 box-content shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1 min-w-0">
            <h3 className="font-semibold">{t('manage_alerts.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('manage_alerts.description')}</p>
          </div>
        </div>
      </div>

      <TooltipProvider>
        <div className="grid min-w-0 grid-cols-1 gap-x-4 @5xl:grid-cols-[auto_minmax(0,2fr)_minmax(0,3fr)_minmax(16rem,3fr)_auto_auto]">
          <section className="col-span-full hidden grid-cols-subgrid items-center rounded-xl bg-muted px-4 py-3 text-sm font-medium text-muted-foreground @5xl:grid">
            <span>{t('manage_alerts.form.header.no')}</span>
            <span className="pl-4">{t('manage_alerts.form.header.name')}</span>
            <span className="pl-4">{t('manage_alerts.form.header.description')}</span>
            <span>{t('manage_alerts.form.header.run_conditions')}</span>
            <span className="text-center">{t('manage_alerts.form.header.active')}</span>
            <span />
          </section>

          {alerts.map((alert, index) => (
            <AlertForm key={alert.id} alert={alert} index={index} />
          ))}

          {!isFull && (
            <section
              data-testid="alert-draft-row"
              className="col-span-full mt-2 grid min-w-0 gap-y-4 rounded-xl border border-border p-4 @5xl:grid-cols-subgrid @5xl:items-start @5xl:rounded-none @5xl:border-0 @5xl:border-t"
            >
              <p className="text-muted-foreground text-sm whitespace-nowrap @5xl:mt-2">
                {t('manage_alerts.form.alert', { index: alerts.length + 1 })}
              </p>
              <Field className="min-w-0">
                <FieldLabel htmlFor="alert-draft-name" className="@5xl:sr-only">
                  {t('manage_alerts.form.header.name')}
                </FieldLabel>
                <Input
                  id="alert-draft-name"
                  placeholder={t('manage_alerts.form.name_placeholder')}
                  size="sm"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  maxLength={100}
                  data-testid="alert-draft-name"
                />
              </Field>
              <Field className="min-w-0">
                <FieldLabel htmlFor="alert-draft-description" className="@5xl:sr-only">
                  {t('manage_alerts.form.header.description')}
                </FieldLabel>
                <AutosizeTextarea
                  id="alert-draft-description"
                  maxLength={500}
                  autoComplete="off"
                  className="rounded-xl !min-h-20 !px-4 pb-2 pt-[9px] leading-snug break-words @5xl:!min-h-[34px]"
                  placeholder={t('manage_alerts.form.description_placeholder')}
                  minHeight={12}
                  value={draftDescription}
                  onChange={(e) => setDraftDescription(e.target.value)}
                  data-testid="alert-draft-description"
                />
              </Field>
              <Field className="min-w-0" aria-labelledby="alert-draft-conditions-label">
                <p id="alert-draft-conditions-label" className="text-sm font-semibold @5xl:sr-only">
                  {t('manage_alerts.form.header.run_conditions')}
                </p>
                <AlertRunConditions
                  conditions={draftRunConditions}
                  isEditing
                  onChange={setDraftRunConditions}
                  testIdPrefix="alert-draft"
                />
              </Field>
              <span className="hidden @5xl:block" />
              <Button
                size="sm"
                variant="dark"
                disabled={!canSubmit}
                onClick={handleAdd}
                className="justify-self-end"
                data-testid="alert-add-button"
              >
                <Plus data-icon="inline-start" />
                {t('button.add_alert')}
              </Button>
            </section>
          )}

          {draftError && (
            <p role="alert" className="col-span-full text-sm text-destructive mt-2">
              {draftError}
            </p>
          )}

          <Separator className="col-span-full mt-4" />
          <div className="col-span-full flex justify-end items-center mt-4">
            <p className="text-sm text-foreground-subtle flex items-center">
              {t('manage_alerts.remaining_alerts')}
              <span className="font-medium ml-1">{remaining}</span>
            </p>
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
};
